<template>
  <div class="status-service-card" :aria-busy="loading ? 'true' : undefined">
    <template v-if="loading">
      <div class="status-service-card__skeleton-name" aria-hidden="true" />
      <div class="status-service-card__skeleton-desc" aria-hidden="true" />
      <div class="status-service-card__skeleton-badge" aria-hidden="true" />
    </template>
    <template v-else>
      <div class="status-service-card__info">
        <p class="status-service-card__name">{{ name }}</p>
        <p class="status-service-card__description">{{ description }}</p>
        <p v-if="disclaimer" class="status-service-card__disclaimer">{{ disclaimer }}</p>
      </div>
      <div
        class="status-service-card__badge"
        :class="`status-service-card__badge--${status}`"
        role="status"
        :aria-label="`${t('status.aria.statusPrefix')} ${badgeLabel}`"
      >
        {{ badgeLabel }}
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { StatusKind } from '@/types/status';

interface Props {
  name: string;
  description: string;
  status: StatusKind;
  loading?: boolean;
  disclaimer?: string;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  disclaimer: undefined
});

const { t } = useI18n();

const badgeLabel = computed(() => {
  if (props.status === 'operational') return t('status.badges.operational');
  if (props.status === 'degraded') return t('status.badges.degraded');
  if (props.status === 'outage') return t('status.badges.outage');
  return t('status.badges.unknown');
});
</script>

<style scoped lang="scss">
.status-service-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-6);
  border: var(--border);
  border-radius: var(--r-4);
  background: var(--paper);

  &__info {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  &__name {
    margin: 0;
    font-size: var(--fs-body);
    font-weight: var(--fw-medium);
    color: var(--ink);
  }

  &__description {
    margin: 0;
    font-size: var(--fs-body-sm);
    color: var(--stone);
  }

  &__badge {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    padding: var(--space-1) var(--space-3);
    border-radius: var(--r-pill);
    font-size: var(--fs-meta);
    font-weight: var(--fw-medium);
    letter-spacing: 0.04em;
    text-transform: uppercase;

    &--operational {
      background: color-mix(in srgb, var(--ok) 12%, transparent);
      color: var(--ok);
    }

    &--degraded {
      background: color-mix(in srgb, var(--warn) 12%, transparent);
      color: var(--warn);
    }

    &--outage {
      background: color-mix(in srgb, var(--danger) 12%, transparent);
      color: var(--danger);
    }

    &--unknown {
      background: color-mix(in srgb, var(--stone) 12%, transparent);
      color: var(--stone);
    }
  }

  &__disclaimer {
    margin: var(--space-1) 0 0;
    font-size: var(--fs-meta);
    color: var(--stone-2);
    font-style: italic;
  }

  // Skeleton states
  &__skeleton-name,
  &__skeleton-desc,
  &__skeleton-badge {
    background: var(--mist);
    border-radius: var(--r-2);
    animation: shimmer 1.4s ease-in-out infinite;
  }

  &__skeleton-name {
    height: 18px;
    width: 140px;
    margin-bottom: var(--space-2);
    border-radius: var(--r-2);
  }

  &__skeleton-desc {
    height: 14px;
    width: 220px;
    border-radius: var(--r-2);
  }

  &__skeleton-badge {
    height: 24px;
    width: 90px;
    border-radius: var(--r-pill);
  }
}

@keyframes shimmer {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

@media (prefers-reduced-motion: reduce) {
  .status-service-card {
    &__skeleton-name,
    &__skeleton-desc,
    &__skeleton-badge {
      animation: none;
    }
  }
}
</style>
